import React, { useCallback, useState, useEffect } from 'react';
import './index.scss';
import { isContrastSufficient, selectNormalBrandLogo } from '@wavvest/shared-utils';
import {
  Button,
  Input,
  Spin,
  Alert,
  ColorPicker,
  Upload,
  Image as AntdImg,
  UploadProps,
  Modal,
  Segmented,
} from 'antd';
import { RcFile, UploadFile } from 'antd/es/upload/interface';
import { UploadOutlined, WarningFilled } from '@ant-design/icons';
import {
  getProfile,
  updateProfile,
  ProfileData,
  GetProfileResponse,
  getPresignedURL,
  uploadProfileMediaByPresignedURLResponse,
} from '@/api/custom';
import { getBrandedActionColors } from '@/util/theme-color';
import WavvestLogoWithText from '@/assets/logo/LogoWithText-Horizontal.svg';
import WavvestLogo from '@/assets/logo/wavvest.svg';
import fileImg from '@/assets/logo/border-file.svg';
import lineChartImg from '@/assets/logo/border-line-chart.svg';
import money from '@/assets/logo/border-money.svg';
import shieldCheck from '@/assets/logo/border-shield-check.svg';
import umbrella from '@/assets/logo/border-umbrella.svg';
import heroBgImg from '@/assets/login-preview/hero-bg.png';
import heroStarsImg from '@/assets/login-preview/hero-stars.png';
import portfolioChartImg from '@/assets/login-preview/portfolio-chart.svg';
import { persistAdvisorLoginBrandContextFromProfile } from '@/pages/login/brandContext';

const DEFAULT_CLIENT_PORTAL_BRAND = 'Client Portal';
const DEFAULT_LANDING_COLOR = '#856095';
const DEFAULT_MOBILE_HOME_COLOR = '#2c2c2c';
const DEFAULT_MOBILE_COMPANY_NAME_COLOR = '#ffffff';

// Brand colors may use transparency, but never below 20% opacity so they stay
// legible across the branded surfaces.
const MIN_COLOR_ALPHA = 0.2;

type BrandColorValue = {
  toRgb: () => { r: number; g: number; b: number; a: number };
  toHexString: () => string;
};

const toHexByte = (channel: number) => Math.round(channel).toString(16).padStart(2, '0');

// Render the picker trigger label to match the Figma "Brand color" component
// (node 20929:14696): the solid hex on the left, a vertical divider, then the
// opacity value as "Opacity NN %". The hex is derived from RGB so the alpha
// channel is dropped from it and surfaced only as the opacity percentage.
const formatBrandColorText = (color: BrandColorValue): React.ReactNode => {
  const { r, g, b, a } = color.toRgb();
  const hex = `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`.toUpperCase();
  return (
    <span className="brand-color-text">
      <span className="brand-color-hex">{hex}</span>
      <span className="brand-color-divider" aria-hidden="true" />
      <span className="brand-color-opacity">Opacity {Math.round(a * 100)} %</span>
    </span>
  );
};

const clampColorAlpha = (color: BrandColorValue): string => {
  const { r, g, b, a } = color.toRgb();
  if (a >= MIN_COLOR_ALPHA) {
    return color.toHexString();
  }
  return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}${toHexByte(Math.round(MIN_COLOR_ALPHA * 255))}`;
};

// Firm alias (ENG-2424) — must match the backend validator in
// src/ria_portal/aws_storage.py::normalize_alias. The client-side check is a
// best-effort UX hint; the server is the source of truth and will return 422
// for malformed input and 409 for collisions.
const FIRM_ALIAS_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const FIRM_ALIAS_MIN_LENGTH = 3;
const FIRM_ALIAS_MAX_LENGTH = 32;
const FIRM_ALIAS_RESERVED = new Set([
  'signup',
  'login',
  'firm',
  'api',
  'admin',
  'wavvest',
  'client',
  'clients',
  'advisor',
  'advisors',
  'auth',
  'portal',
  'public',
  'static',
  'assets',
  'health',
  'www',
]);
const FIRM_ALIAS_FORMAT_MESSAGE =
  'Alias must be 3-32 lowercase letters, digits, or hyphens (no leading, trailing, or double hyphens) and cannot be a reserved word.';
const FIRM_ALIAS_TAKEN_MESSAGE = 'This firm alias is already in use. Please pick a different one.';

const validateFirmAlias = (rawAlias: string): string | null => {
  const trimmed = rawAlias.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  if (trimmed.length < FIRM_ALIAS_MIN_LENGTH || trimmed.length > FIRM_ALIAS_MAX_LENGTH) {
    return FIRM_ALIAS_FORMAT_MESSAGE;
  }
  if (!FIRM_ALIAS_PATTERN.test(trimmed)) {
    return FIRM_ALIAS_FORMAT_MESSAGE;
  }
  if (FIRM_ALIAS_RESERVED.has(trimmed)) {
    return FIRM_ALIAS_FORMAT_MESSAGE;
  }
  return null;
};

const extractServerErrorMessage = (err: unknown): string | null => {
  if (!err || typeof err !== 'object') {
    return null;
  }
  const response = (err as { response?: { data?: { detail?: unknown } } }).response;
  const detail = response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  return null;
};

const getProfileSaveErrorState = (
  err: unknown,
): { aliasError: string | null; generalError: string | null } => {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  const detail = extractServerErrorMessage(err);
  const isAliasRelated = /alias/i.test(detail ?? '');
  if (status === 409) {
    return {
      aliasError: detail || FIRM_ALIAS_TAKEN_MESSAGE,
      generalError: null,
    };
  }
  if (status === 422 && isAliasRelated) {
    return {
      aliasError: detail || FIRM_ALIAS_FORMAT_MESSAGE,
      generalError: null,
    };
  }
  return {
    aliasError: null,
    generalError: detail || 'Failed to save company settings. Please try again.',
  };
};

const getPreviewAssetUrl = (url?: string | null) => {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) {
    return '';
  }

  return `${trimmedUrl}${trimmedUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
};

type PortalPreviewVariant = 'header' | 'login' | 'signup';
type PortalPreviewViewport = 'desktop' | 'mobile';
type PortalPreviewTheme = 'dark' | 'light';

type ClientPortalBrandingPreviewProps = {
  backgroundUrl: string;
  lightTextLogoUrl: string;
  profile: ProfileData | null;
  logoUrl: string;
  textLogoUrl: string;
  variant: PortalPreviewVariant;
  viewport: PortalPreviewViewport;
  themeMode: PortalPreviewTheme;
  heroBackgroundImage?: string;
};

const toCssUrl = (url: string): string => `url("${url.replace(/"/g, '\\"')}")`;

const isTransparentPreviewColor = (color?: string | null): boolean => {
  const normalizedColor = color?.trim().toLowerCase();
  if (!normalizedColor || normalizedColor === 'transparent') {
    return true;
  }

  if (/^#[0-9a-f]{4}$/.test(normalizedColor)) {
    return parseInt(normalizedColor[4], 16) < 15;
  }

  if (/^#[0-9a-f]{8}$/.test(normalizedColor)) {
    return parseInt(normalizedColor.slice(7, 9), 16) < 255;
  }

  const alphaMatch = normalizedColor.match(/^(?:rgba|hsla)\([^)]*,\s*([0-9.]+)\s*\)$/);
  if (!alphaMatch) {
    return false;
  }

  return Number(alphaMatch[1]) < 1;
};

const getSolidPreviewColor = (color: string | undefined | null, fallback: string): string =>
  isTransparentPreviewColor(color) ? fallback : color!.trim();

const createCurrentImageFileList = (url: string | null | undefined, name: string): UploadFile[] => {
  const previewUrl = getPreviewAssetUrl(url);
  if (!previewUrl) {
    return [];
  }

  return [
    {
      uid: `current-${name}`,
      name,
      status: 'done',
      url: previewUrl,
    },
  ];
};

// The authentication previews mimic the real advisor/client login hero, which
// layers the stars overlay over the hero background image rather than the
// firm's landing background image.
const AUTH_PREVIEW_HERO_BACKGROUND = `${toCssUrl(heroStarsImg)}, ${toCssUrl(heroBgImg)}`;

const getPreviewBrandActionStyle = ({
  brandColor,
  surfaceColor,
  pageBackgroundColor,
}: {
  brandColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
}) => {
  const actionColors = getBrandedActionColors({
    primaryColor: brandColor,
    surfaceColor,
    pageBackgroundColor,
  });

  return {
    '--brand-color': brandColor,
    '--brand-action-color': actionColors.backgroundColor,
    '--brand-action-foreground': actionColors.foregroundColor,
  } as React.CSSProperties;
};

const getPortalPreviewStyle = (
  landingColor?: string,
  backgroundUrl?: string,
  heroBackgroundImage?: string,
  themeMode: PortalPreviewTheme = 'dark',
) =>
  ({
    ...getPreviewBrandActionStyle({
      brandColor: landingColor || DEFAULT_LANDING_COLOR,
      surfaceColor: themeMode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
      pageBackgroundColor: themeMode === 'light' ? '#f0f0f0' : '#000000',
    }),
    '--preview-background-image': heroBackgroundImage
      ? heroBackgroundImage
      : backgroundUrl
        ? toCssUrl(backgroundUrl)
        : 'none',
    '--preview-portfolio-background-image': backgroundUrl ? toCssUrl(backgroundUrl) : 'none',
  }) as React.CSSProperties;

const PreviewBrand: React.FC<{
  profile: ProfileData | null;
  textLogoUrl: string;
}> = ({ profile, textLogoUrl }) => {
  if (textLogoUrl) {
    return (
      <img
        src={textLogoUrl}
        alt={profile?.name || DEFAULT_CLIENT_PORTAL_BRAND}
        className="portal-preview-brand-image"
      />
    );
  }

  return <div className="portal-preview-brand-text">{DEFAULT_CLIENT_PORTAL_BRAND}</div>;
};

const PreviewLogo: React.FC<{
  profile: ProfileData | null;
  logoUrl: string;
  fallbackTextLogoUrl?: string;
}> = ({ profile, logoUrl, fallbackTextLogoUrl = '' }) => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${profile?.name || DEFAULT_CLIENT_PORTAL_BRAND} logo`}
        className="portal-preview-logo-image"
      />
    );
  }

  if (fallbackTextLogoUrl) {
    return <PreviewBrand profile={profile} textLogoUrl={fallbackTextLogoUrl} />;
  }

  return null;
};

const PreviewField: React.FC<{ label: string; value?: string }> = ({ label, value = '' }) => (
  <div className="portal-preview-field">
    <div className="portal-preview-field-label">{label}</div>
    <div className="portal-preview-field-input">{value}</div>
  </div>
);

const PortfolioPreviewCard: React.FC<{ hasBackground?: boolean }> = ({ hasBackground = false }) => {
  // When the firm has uploaded a landing background image, the card becomes a
  // pure image surface (the image is painted by the `::before` layer in SCSS),
  // so the placeholder portfolio summary and accounts are hidden.
  if (hasBackground) {
    return (
      <div
        className="portal-preview-portfolio-card portal-preview-portfolio-card--image-only"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="portal-preview-portfolio-card" aria-hidden="true">
      <div className="portal-preview-portfolio-summary">
        <div className="portal-preview-portfolio-row">
          <div className="portal-preview-portfolio-info">
            <div className="portal-preview-portfolio-label-row">
              <span className="portal-preview-portfolio-label">MANAGED PORTFOLIO</span>
              <span className="portal-preview-portfolio-live">
                <span className="portal-preview-portfolio-live-dot" />
                LIVE
              </span>
            </div>
            <div className="portal-preview-portfolio-value">$1,037,400</div>
            <div className="portal-preview-portfolio-delta-row">
              <span className="portal-preview-portfolio-delta-pill">▲ +0.76%</span>
              <span className="portal-preview-portfolio-delta-today">$7,820 today</span>
            </div>
          </div>
          <div className="portal-preview-portfolio-tabs">
            <span className="tab active">1M</span>
            <span className="tab">3M</span>
            <span className="tab">1Y</span>
            <span className="tab">All</span>
          </div>
        </div>
        <img className="portal-preview-portfolio-chart" src={portfolioChartImg} alt="" />
      </div>
      <div className="portal-preview-portfolio-accounts">
        {[
          { name: 'Taxable Brokerage', amount: '$622,400', delta: '+0.8%' },
          { name: 'Traditional IRA', amount: '$340,100', delta: '+0.5%' },
          { name: 'Roth IRA', amount: '$189,200', delta: '+1.2%' },
        ].map((account) => (
          <div className="portal-preview-portfolio-account-row" key={account.name}>
            <div className="portal-preview-portfolio-account-info">
              <span className="portal-preview-portfolio-account-icon" />
              <span className="portal-preview-portfolio-account-name">{account.name}</span>
            </div>
            <div className="portal-preview-portfolio-account-amounts">
              <span className="amount">{account.amount}</span>
              <span className="delta">{account.delta}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PreviewBrandRow: React.FC<{
  profile: ProfileData | null;
  logoUrl: string;
  textLogoUrl: string;
}> = ({ profile, logoUrl, textLogoUrl }) => (
  <div className="portal-preview-brand-row">
    <PreviewLogo profile={profile} logoUrl={logoUrl} fallbackTextLogoUrl={textLogoUrl} />
  </div>
);

const LoginPreviewBody: React.FC<{
  profile: ProfileData | null;
  logoUrl: string;
  textLogoUrl: string;
  hasBackground?: boolean;
}> = ({ profile, logoUrl, textLogoUrl, hasBackground = false }) => (
  <>
    <div className="portal-preview-form-panel">
      <PreviewBrandRow profile={profile} logoUrl={logoUrl} textLogoUrl={textLogoUrl} />
      <div className="portal-preview-form-center">
        <div className="portal-preview-title">Welcome Back</div>
        <div className="portal-preview-form-fields">
          <PreviewField label="Email" value="client@example.com" />
          <PreviewField label="Password" value="**********" />
          <div className="portal-preview-form-row">
            <span className="portal-preview-check" />
            <span>Remember me</span>
            <span className="portal-preview-link">Forgot password?</span>
          </div>
          <div className="portal-preview-primary-button">Sign In</div>
          <div className="portal-preview-helper-link">Having trouble signing in?</div>
        </div>
      </div>
      <div className="portal-preview-disclaimer">
        Advisory services provided by Wavvest Wealth LLC.
      </div>
    </div>
    <div className="portal-preview-hero-side">
      <div className="portal-preview-hero-sheen" />
      <div className="portal-preview-hero-headline">Holistic wealth management.</div>
      <PortfolioPreviewCard hasBackground={hasBackground} />
    </div>
  </>
);

const SignupPreviewBody: React.FC<{
  companyName: string;
  compact: boolean;
  logoUrl: string;
  profile: ProfileData | null;
  textLogoUrl: string;
  hasBackground?: boolean;
}> = ({ companyName, compact, logoUrl, profile, textLogoUrl, hasBackground = false }) => {
  const introduction = profile?.introduction?.trim();
  const heroHeadline = introduction || `Welcome to ${companyName}.`;

  return (
    <>
      <div className="portal-preview-form-panel portal-preview-signup-body">
        <PreviewBrandRow profile={profile} logoUrl={logoUrl} textLogoUrl={textLogoUrl} />
        <div className="portal-preview-form-center">
          <div className="portal-preview-title">Welcome to {companyName}</div>
          {introduction && <div className="portal-preview-introduction">{introduction}</div>}
          <div className="portal-preview-subtitle">Create your account to get started</div>
          <div className="portal-preview-signup-fields">
            <PreviewField label="Email" value="client@example.com" />
            <PreviewField label="Password" value="**********" />
            {!compact && <PreviewField label="Confirm Password" value="**********" />}
          </div>
          <div className="portal-preview-terms">
            <span className="portal-preview-check" />
            <span>I agree to the Terms of Use</span>
          </div>
          <div className="portal-preview-actions">
            <div className="portal-preview-secondary-button">Cancel</div>
            <div className="portal-preview-primary-button">Create Account</div>
          </div>
          <div className="portal-preview-footer">
            <span>Already have an account?</span>
            <span className="portal-preview-link">Sign In</span>
          </div>
        </div>
      </div>
      <div className="portal-preview-hero-side">
        <div className="portal-preview-hero-sheen" />
        <div className="portal-preview-hero-headline">{heroHeadline}</div>
        <PortfolioPreviewCard hasBackground={hasBackground} />
      </div>
    </>
  );
};

const HeaderPreviewBody: React.FC<{
  profile: ProfileData | null;
  textLogoUrl: string;
  viewport: PortalPreviewViewport;
}> = ({ profile, textLogoUrl, viewport }) => (
  <div className="portal-preview-header-body">
    <div className="portal-preview-client-header">
      <div className="portal-preview-logo-container">
        <PreviewBrand profile={profile} textLogoUrl={textLogoUrl} />
      </div>
      {viewport === 'desktop' ? (
        <div className="portal-preview-header-nav">
          <span>Home</span>
          <span>Planning</span>
          <span>Documents</span>
        </div>
      ) : (
        <div className="portal-preview-menu-icon">
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
    <div className="portal-preview-header-content">
      <div className="portal-preview-content-kicker">Portfolio</div>
      <div className="portal-preview-content-title">$200,600.66</div>
      <div className="portal-preview-content-row" />
      <div className="portal-preview-content-row short" />
    </div>
  </div>
);

const ClientPortalBrandingPreview: React.FC<ClientPortalBrandingPreviewProps> = ({
  backgroundUrl,
  lightTextLogoUrl,
  profile,
  logoUrl,
  textLogoUrl,
  variant,
  viewport,
  themeMode,
  heroBackgroundImage,
}) => {
  const companyName = profile?.co_branding && profile.name ? profile.name : 'Wavvest';
  const isLogin = variant === 'login';
  const isHeader = variant === 'header';
  const hasBackground = Boolean(backgroundUrl);
  const headerTextLogoUrl =
    themeMode === 'light' ? lightTextLogoUrl || textLogoUrl : textLogoUrl || lightTextLogoUrl;

  return (
    <div
      className={`portal-preview-card ${variant} ${viewport} theme-${themeMode}`}
      data-testid={`portal-preview-${variant}-${viewport}`}
      style={getPortalPreviewStyle(
        profile?.landing_color,
        backgroundUrl,
        heroBackgroundImage,
        themeMode,
      )}
    >
      <div className="portal-preview-surface">
        {isHeader ? (
          <HeaderPreviewBody
            profile={profile}
            textLogoUrl={headerTextLogoUrl}
            viewport={viewport}
          />
        ) : isLogin ? (
          <LoginPreviewBody
            profile={profile}
            logoUrl={logoUrl}
            textLogoUrl={textLogoUrl}
            hasBackground={hasBackground}
          />
        ) : (
          <SignupPreviewBody
            companyName={companyName}
            compact={viewport === 'mobile'}
            logoUrl={logoUrl}
            profile={profile}
            textLogoUrl={textLogoUrl}
            hasBackground={hasBackground}
          />
        )}
      </div>
    </div>
  );
};

// ===== Live preview: shared theme + brand-color frame =====
// New advisor/client preview surfaces consume the brand color (`--brand-color`)
// and resolve their own light/dark tokens, so the Dark/Light toggle and the
// firm's primary color update them in real time.
const LivePreviewFrame: React.FC<{
  themeMode: PortalPreviewTheme;
  brandColor: string;
  className?: string;
  children: React.ReactNode;
}> = ({ themeMode, brandColor, className = '', children }) => (
  <div
    className={`lp-surface theme-${themeMode}${className ? ` ${className}` : ''}`}
    style={getPreviewBrandActionStyle({
      brandColor,
      surfaceColor: themeMode === 'light' ? '#f8fafc' : '#16161c',
      pageBackgroundColor: themeMode === 'light' ? '#ffffff' : '#0b0b0f',
    })}
  >
    {children}
  </div>
);

// Theme-aware brand mark used inside the new advisor/client previews. Falls
// back to the firm name (or Wavvest) when no logo asset is configured.
const LivePreviewBrandMark: React.FC<{
  profile: ProfileData | null;
  logoUrl: string;
}> = ({ profile, logoUrl }) => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${profile?.name || DEFAULT_CLIENT_PORTAL_BRAND} logo`}
        className="lp-brand-mark-img"
      />
    );
  }

  return (
    <span className="lp-brand-mark-text">
      {profile?.co_branding && profile.name ? profile.name : 'Wavvest'}
    </span>
  );
};

const ADVISOR_NAV_ITEMS = [
  { label: 'Dashboard', active: true },
  { label: 'Clients', active: false },
  { label: 'Planning', active: false },
  { label: 'Documents', active: false },
  { label: 'Settings', active: false },
];

// Advisor portal sidebar: shows the firm logo plus navigation highlights and a
// primary call-to-action, all driven by the brand color.
const AdvisorNavBrandingPreview: React.FC<{
  profile: ProfileData | null;
  logoUrl: string;
  themeMode: PortalPreviewTheme;
  brandColor: string;
}> = ({ profile, logoUrl, themeMode, brandColor }) => (
  <LivePreviewFrame themeMode={themeMode} brandColor={brandColor} className="lp-advisor-nav">
    <div className="lp-sidebar">
      <div className="lp-sidebar-logo">
        <LivePreviewBrandMark profile={profile} logoUrl={logoUrl} />
      </div>
      <div className="lp-sidebar-items">
        {ADVISOR_NAV_ITEMS.map((item) => (
          <div key={item.label} className={`lp-nav-item${item.active ? ' active' : ''}`}>
            <span className="lp-nav-icon" />
            <span className="lp-nav-label">{item.label}</span>
          </div>
        ))}
      </div>
      <div className="lp-primary-btn lp-sidebar-cta">New Client</div>
    </div>
    <div className="lp-sidebar-canvas">
      <div className="lp-canvas-title">How can we help you today, Jacob?</div>
      <div className="lp-canvas-input">
        <span className="lp-canvas-placeholder">Ask anything…</span>
        <span className="lp-canvas-send" />
      </div>
      <div className="lp-canvas-hint">
        Brand color highlights active navigation, icons, and primary actions.
      </div>
    </div>
  </LivePreviewFrame>
);

const BRAND_USAGE_ROWS = [
  { name: 'Sarah Johnson', status: 'Active', action: 'Show insight', link: true },
  { name: 'Michael Chen', status: 'Active', action: '3 days ago', link: false },
  { name: 'Emma Wilson', status: 'Active', action: '5 days ago', link: false },
  { name: 'David Rodriguez', status: 'Prospect', action: 'Schedule review', link: true },
  { name: 'Lisa Martin', status: 'Active', action: '7 days ago', link: false },
];

// Advisor portal clients table: demonstrates brand color on the active filter
// tab, primary button, table links, borders, and selection states.
const BrandColorUsagePreview: React.FC<{
  themeMode: PortalPreviewTheme;
  brandColor: string;
}> = ({ themeMode, brandColor }) => (
  <LivePreviewFrame themeMode={themeMode} brandColor={brandColor} className="lp-table-card">
    <div className="lp-table-toolbar">
      <div className="lp-pill-group">
        <span className="lp-pill active">All Clients</span>
        <span className="lp-pill">Active</span>
        <span className="lp-pill">Prospects</span>
      </div>
      <div className="lp-toolbar-actions">
        <span className="lp-search" />
        <span className="lp-primary-btn">Convert to WealthPlan</span>
      </div>
    </div>
    <div className="lp-table">
      <div className="lp-table-head lp-cols-3">
        <span>Name</span>
        <span>Status</span>
        <span>Last Activity</span>
      </div>
      {BRAND_USAGE_ROWS.map((row) => (
        <div key={row.name} className="lp-table-row lp-cols-3">
          <span className="lp-cell-name">
            <span className="lp-avatar" />
            {row.name}
          </span>
          <span className={`lp-status${row.status === 'Active' ? ' active' : ''}`}>
            {row.status}
          </span>
          <span className={`lp-cell-action${row.link ? ' lp-link' : ''}`}>{row.action}</span>
        </div>
      ))}
    </div>
  </LivePreviewFrame>
);

const CLIENT_NAV_LINKS = [
  { label: 'Dashboard', active: false },
  { label: 'Wealth', active: true },
  { label: 'Planning', active: false },
  { label: 'Files', active: false },
  { label: 'Tasks', active: false },
  { label: 'Messages', active: false },
];

const CLIENT_HOLDINGS = [
  { security: 'Google (GOOG)', value: '$2,500,000', weight: '63.4%' },
  { security: 'Primary Residence', value: '$1,000,000', weight: '25.4%' },
  { security: 'S&P 500 ETF (SPY)', value: '$225,000', weight: '5.7%' },
  { security: 'Tech ETF (QQQ)', value: '$95,000', weight: '2.4%' },
];

// Client portal wealth dashboard: shows the header logo plus brand color on the
// active nav link, content tabs, filter pills, and the primary action button.
const ClientPortalHeaderPreview: React.FC<{
  profile: ProfileData | null;
  textLogoUrl: string;
  themeMode: PortalPreviewTheme;
  brandColor: string;
}> = ({ profile, textLogoUrl, themeMode, brandColor }) => (
  <LivePreviewFrame themeMode={themeMode} brandColor={brandColor} className="lp-client-portal">
    <div className="lp-topnav">
      <div className="lp-topnav-logo">
        <LivePreviewBrandMark profile={profile} logoUrl={textLogoUrl} />
      </div>
      <div className="lp-topnav-links">
        {CLIENT_NAV_LINKS.map((link) => (
          <span key={link.label} className={`lp-topnav-link${link.active ? ' active' : ''}`}>
            {link.label}
          </span>
        ))}
        <span className="lp-topnav-bell" />
      </div>
    </div>
    <div className="lp-client-body">
      <div className="lp-client-head">
        <div>
          <div className="lp-client-kicker">Current Net Worth</div>
          <div className="lp-client-value">$3.43M</div>
        </div>
        <div className="lp-client-actions">
          <span className="lp-primary-btn">Manage Your Money</span>
          <span className="lp-ghost-btn">Connected Accounts</span>
          <span className="lp-ghost-btn">Open an Account</span>
        </div>
      </div>
      <div className="lp-content-tabs">
        <span className="lp-content-tab">Portfolio Summary</span>
        <span className="lp-content-tab">Asset Allocation</span>
        <span className="lp-content-tab active">Holdings</span>
      </div>
      <div className="lp-pill-group">
        <span className="lp-pill active">All Institutions</span>
        <span className="lp-pill">Wavvest</span>
        <span className="lp-pill">Google</span>
      </div>
      <div className="lp-table">
        <div className="lp-table-head lp-cols-3">
          <span>Security</span>
          <span>Value</span>
          <span>Weight</span>
        </div>
        {CLIENT_HOLDINGS.map((holding) => (
          <div key={holding.security} className="lp-table-row lp-cols-3">
            <span className="lp-cell-name">{holding.security}</span>
            <span>{holding.value}</span>
            <span>{holding.weight}</span>
          </div>
        ))}
      </div>
    </div>
  </LivePreviewFrame>
);

type LivePreviewTab = 'authentication' | 'advisor-portal' | 'client-portal';

const LIVE_PREVIEW_TABS: { key: LivePreviewTab; label: string }[] = [
  { key: 'authentication', label: 'Authentication' },
  { key: 'advisor-portal', label: 'Advisor portal' },
  { key: 'client-portal', label: 'Client Portal' },
];

type CompanySettingsTab = 'brand-identity' | 'authentication' | 'logos' | 'brand-colors';

const COMPANY_SETTINGS_TABS: { key: CompanySettingsTab; label: string }[] = [
  { key: 'brand-identity', label: 'Brand Identity' },
  { key: 'authentication', label: 'Authentication' },
  { key: 'logos', label: 'Logos' },
  { key: 'brand-colors', label: 'Brand Colors' },
];

// The backend only ever stores a resolved `light` or `dark` value. Dark is the
// product default, so the picker surfaces just the two real options and labels
// the dark swatch as the default rather than repeating a separate "System
// default" entry that resolves to the same thing.
type DefaultThemeSelection = 'dark' | 'light';

const DEFAULT_THEME_OPTIONS: {
  key: DefaultThemeSelection;
  label: string;
  variant: 'dark' | 'light';
}[] = [
  { key: 'dark', label: 'Dark (Default)', variant: 'dark' },
  { key: 'light', label: 'Light', variant: 'light' },
];

// ===== Shared asset spec / usage meta (matches the Figma "Recommended …" blue
// spec line and the "USED IN" Advisor/Client portal usage lists) =====
type AssetUsageGroup = { portal: string; items: string[] };

const PRIMARY_LOGO_SPEC = 'Recommended: SVG / transparent background / Display size: 180×30';
const COMPACT_LOGO_SPEC = 'Recommended: SVG / 1:1 / transparent background / Display size: 25x25';

const PRIMARY_LOGO_USAGE: AssetUsageGroup[] = [
  {
    portal: 'Advisor Portal',
    items: ['Sign Up/Login page · Web & Mobile', 'Expanded Sidebar · Web'],
  },
  { portal: 'Client Portal', items: ['Sign Up/Login page · Web & Mobile', 'Sidebar · Web'] },
];
const COMPACT_LOGO_USAGE: AssetUsageGroup[] = [
  {
    portal: 'Advisor Portal',
    items: ['Collapsed Sidebar · Web'],
  },
];
const LOGIN_BACKGROUND_USAGE: AssetUsageGroup[] = [
  { portal: 'Advisor Portal', items: ['Sign Up page · Web & Mobile'] },
  { portal: 'Client Portal', items: ['Login page · Web & Mobile'] },
];

// The Figma only specs the four primary/compact slots. The header (legacy text)
// logo and the mobile mark are extra fields the design doesn't cover, but they
// mirror the two spec'd archetypes: the header logo is a horizontal wordmark
// (so it reuses the primary 160×40 recommendation) and the mark is a square
// glyph (so it reuses the compact 512×512 / 1:1 recommendation). Their usage is
// derived from where the code actually consumes the URIs, not the Figma slots.
const HEADER_LOGO_SPEC = PRIMARY_LOGO_SPEC;
const MARK_LOGO_SPEC = COMPACT_LOGO_SPEC;

// text_logo_uri / light_text_logo_uri: the client portal header wordmark. The
// authenticated top nav prefers the primary/compact logo and falls back to this
// legacy asset; it also backs the client login/signup wordmark.
const HEADER_LOGO_USAGE: AssetUsageGroup[] = [
  {
    portal: 'Client Portal',
    items: ['Top Navigation · Web & Mobile'],
  },
];

// logo_mark_uri: square brand mark used for account & institution icons in the
// Wealth views across both portals.
const MARK_LOGO_USAGE: AssetUsageGroup[] = [
  { portal: 'Advisor Portal', items: ['Account & institution icons · Web & Mobile'] },
  { portal: 'Client Portal', items: ['Account & institution icons · Web & Mobile'] },
];

const AssetSpec: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="asset-spec">{children}</div>
);

const AssetUsage: React.FC<{ groups: AssetUsageGroup[] }> = ({ groups }) => (
  <div className="asset-usage">
    <div className="asset-usage-title">Used in</div>
    <div className="asset-usage-groups">
      {groups.map((group) => (
        <div className="asset-usage-group" key={group.portal}>
          <div className="asset-usage-portal">{group.portal}</div>
          <ul className="asset-usage-items">
            {group.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const AppliedTo: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="applied-to">
    <div className="applied-to-title">Applied to</div>
    <ul className="applied-to-items">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

// Upload trigger matching the Figma drop-zone (node 21502:23580): a dashed
// rounded box with the upload glyph stacked above the "Upload" label, centered.
const UploadTrigger: React.FC = () => (
  <div className="upload-trigger">
    <UploadOutlined className="upload-trigger-icon" />
    <span className="upload-trigger-label">Upload</span>
  </div>
);

const ThemePreviewMock: React.FC<{ variant: 'dark' | 'light' }> = ({ variant }) => (
  <span className={`theme-preview theme-preview--${variant}`} aria-hidden="true">
    <span className="theme-preview-topbar">
      <span className="theme-preview-dot" />
      <span className="theme-preview-dot" />
      <span className="theme-preview-dot" />
    </span>
    <span className="theme-preview-body">
      <span className="theme-preview-sidebar">
        <span className="theme-preview-logo">
          <span className="theme-preview-mark" />
          <span className="theme-preview-wordmark" />
        </span>
        <span className="theme-preview-menu">
          <span />
          <span />
          <span />
          <span />
          <span />
        </span>
      </span>
      <span className="theme-preview-main">
        <span className="theme-preview-heading">Your dashboard</span>
        <span className="theme-preview-subline" />
        <span className="theme-preview-chart" />
      </span>
    </span>
  </span>
);

const CompanySettings: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<CompanySettingsTab>('brand-identity');
  const [themeSelection, setThemeSelection] = useState<DefaultThemeSelection>('dark');

  const [logoUrl, setLogoUrl] = useState('');
  const [logoMarkUrl, setLogoMarkUrl] = useState('');
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [portalPreviewTheme, setPortalPreviewTheme] = useState<PortalPreviewTheme>('dark');
  const [livePreviewTab, setLivePreviewTab] = useState<LivePreviewTab>('authentication');

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [edit, setEdit] = useState<boolean>(false);

  const [previewLogoOpen, setPreviewLogooOpen] = useState(false);
  const [previewLogoLightOpen, setPreviewLogoLightOpen] = useState(false);
  const [previewLogooMarkOpen, setPreviewLogooMarkOpen] = useState(false);
  const [previewCompactLogoOpen, setPreviewCompactLogoOpen] = useState(false);
  const [previewCompactLogoLightOpen, setPreviewCompactLogoLightOpen] = useState(false);
  const [previewTextLogoOpen, setPreviewTextLogoOpen] = useState(false);
  const [previewLightTextLogoOpen, setPreviewLightTextLogoOpen] = useState(false);
  const [previewBackgroundOpen, setPreviewBackgroundOpen] = useState(false);
  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);
  const [logoLightFileList, setLogoLightFileList] = useState<UploadFile[]>([]);
  const [logoMarkFileList, setLogooMarkFileList] = useState<UploadFile[]>([]);
  const [compactLogoFileList, setCompactLogoFileList] = useState<UploadFile[]>([]);
  const [compactLogoLightFileList, setCompactLogoLightFileList] = useState<UploadFile[]>([]);
  const [textLogoFileList, setTextLogoFileList] = useState<UploadFile[]>([]);
  const [lightTextLogoFileList, setLightTextLogoFileList] = useState<UploadFile[]>([]);
  const [backgroundFileList, setBackgroundFileList] = useState<UploadFile[]>([]);

  const applyProfileData = useCallback((nextProfile: ProfileData) => {
    persistAdvisorLoginBrandContextFromProfile(nextProfile);
    setProfile(nextProfile);
    setThemeSelection(nextProfile.default_theme === 'light' ? 'light' : 'dark');
    setLogoUrl(nextProfile.logo_uri);
    setLogoMarkUrl(nextProfile.logo_mark_uri);
    setBackgroundUrl(nextProfile.background_uri);
    setLogoFileList(createCurrentImageFileList(nextProfile.logo_uri, 'Landing logo'));
    setLogoLightFileList(
      createCurrentImageFileList(nextProfile.logo_light_uri, 'Landing logo (light)'),
    );
    setLogooMarkFileList(
      createCurrentImageFileList(nextProfile.logo_mark_uri, 'Compact logo mark'),
    );
    setCompactLogoFileList(
      createCurrentImageFileList(nextProfile.compact_logo_uri, 'Compact logo'),
    );
    setCompactLogoLightFileList(
      createCurrentImageFileList(nextProfile.compact_logo_light_uri, 'Compact logo (light)'),
    );
    setTextLogoFileList(createCurrentImageFileList(nextProfile.text_logo_uri, 'Dark header logo'));
    setLightTextLogoFileList(
      createCurrentImageFileList(nextProfile.light_text_logo_uri, 'Light header logo'),
    );
    setBackgroundFileList(
      createCurrentImageFileList(nextProfile.background_uri, 'Landing background image'),
    );
  }, []);

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      const profileData: GetProfileResponse = await getProfile();
      applyProfileData(profileData.profile);
    } catch {
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [applyProfileData]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const toggleEdit = () => {
    setEdit((prevEdit) => !prevEdit);
  };

  const handleThemeSelect = (selection: DefaultThemeSelection) => {
    setThemeSelection(selection);
    setProfile((prev) => (prev ? { ...prev, default_theme: selection } : prev));
  };

  const handleSave = async () => {
    if (edit) {
      const aliasValidationError = validateFirmAlias(profile?.alias ?? '');
      if (aliasValidationError) {
        setAliasError(aliasValidationError);
        return;
      }
      setAliasError(null);
      const customizeColor = profile?.customize_color || '';
      const nameColor = profile?.name_color || '';
      const isCorret = isContrastSufficient(customizeColor, nameColor);
      if (!isCorret) {
        Modal.confirm({
          title: 'The color setting might not be appropriate.',
          content: `Are you sure you want to continue?`,
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: async () => {
            try {
              setLoading(true);
              const profileData: GetProfileResponse = await updateProfile(profile as ProfileData);
              setEdit(false);
              applyProfileData(profileData.profile);
            } catch (err) {
              const { aliasError: nextAliasError, generalError } = getProfileSaveErrorState(err);
              setAliasError(nextAliasError);
              setError(generalError);
            } finally {
              setLoading(false);
            }
          },
        });
      } else {
        try {
          setLoading(true);
          const profileData: GetProfileResponse = await updateProfile(profile as ProfileData);
          setEdit(false);
          applyProfileData(profileData.profile);
        } catch (err) {
          const { aliasError: nextAliasError, generalError } = getProfileSaveErrorState(err);
          setAliasError(nextAliasError);
          setError(generalError);
        } finally {
          setLoading(false);
        }
      }
    } else {
      toggleEdit();
    }
  };

  const cancelEdit = () => {
    setEdit(false);
    setAliasError(null);
    fetchProfileData();
  };

  const handleFileUpload = async (
    options: {
      file: RcFile;
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    },
    mediaType:
      | 'logo'
      | 'logo_light'
      | 'logo_mark'
      | 'compact_logo'
      | 'compact_logo_light'
      | 'background'
      | 'text_logo'
      | 'light_text_logo',
  ) => {
    try {
      const file = options.file as File;
      const uploadUrlobj: uploadProfileMediaByPresignedURLResponse = await getPresignedURL({
        file_name: file.name,
        media_type: mediaType,
        file_type: file.type,
      });

      if (!uploadUrlobj.presigned_url) {
        throw new Error('Unable to get presigned URL');
      }

      const formData = new FormData();
      Object.entries(uploadUrlobj.form_fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrlobj.presigned_url, true);

      xhr.onload = () => {
        if (xhr.status === 200) {
          options.onSuccess?.();
        } else {
          options.onError?.(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => {
        options.onError?.(new Error('Upload failed'));
      };

      xhr.send(formData);

      const fileUrl = uploadUrlobj.url;

      switch (mediaType) {
        case 'logo':
          setLogoUrl(`${fileUrl}?t=${Date.now()}`);
          break;
        case 'logo_light':
          break;
        case 'logo_mark':
          setLogoMarkUrl(`${fileUrl}?t=${Date.now()}`);
          break;
        case 'compact_logo':
          break;
        case 'compact_logo_light':
          break;
        case 'text_logo':
          break;
        case 'light_text_logo':
          break;
        case 'background':
          setBackgroundUrl(`${fileUrl}?t=${Date.now()}`);
          break;
      }

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              [`${mediaType}_uri`]: fileUrl,
            }
          : null,
      );
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleLogoUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setLogoFileList(newFileList);
    if (newFileList.length === 0) {
      setLogoUrl('');
      setProfile((prev) => (prev ? { ...prev, logo_uri: '' } : prev));
    }
  };

  const handleLogoLightUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setLogoLightFileList(newFileList);
    if (newFileList.length === 0) {
      setProfile((prev) => (prev ? { ...prev, logo_light_uri: '' } : prev));
    }
  };

  const handleLogooMarkUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setLogooMarkFileList(newFileList);
    if (newFileList.length === 0) {
      setLogoMarkUrl('');
      setProfile((prev) => (prev ? { ...prev, logo_mark_uri: '' } : prev));
    }
  };

  const handleCompactLogoUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setCompactLogoFileList(newFileList);
    if (newFileList.length === 0) {
      setProfile((prev) => (prev ? { ...prev, compact_logo_uri: '' } : prev));
    }
  };

  const handleCompactLogoLightUploadChange: UploadProps['onChange'] = ({
    fileList: newFileList,
  }) => {
    setCompactLogoLightFileList(newFileList);
    if (newFileList.length === 0) {
      setProfile((prev) => (prev ? { ...prev, compact_logo_light_uri: '' } : prev));
    }
  };

  const handleTextLogoUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setTextLogoFileList(newFileList);
    if (newFileList.length === 0) {
      setProfile((prev) => (prev ? { ...prev, text_logo_uri: '' } : prev));
    }
  };

  const handleLightTextLogoUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setLightTextLogoFileList(newFileList);
    if (newFileList.length === 0) {
      setProfile((prev) => (prev ? { ...prev, light_text_logo_uri: '' } : prev));
    }
  };

  const handleBackgroundUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setBackgroundFileList(newFileList);
    if (newFileList.length === 0) {
      setBackgroundUrl('');
      setProfile((prev) => (prev ? { ...prev, background_uri: '' } : prev));
    }
  };

  const handleLogoPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewLogooOpen(true);
  };

  const handleLogoLightPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewLogoLightOpen(true);
  };

  const handleLogooMarkPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewLogooMarkOpen(true);
  };

  const handleCompactLogoPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewCompactLogoOpen(true);
  };

  const handleCompactLogoLightPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewCompactLogoLightOpen(true);
  };

  const handleTextLogoPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewTextLogoOpen(true);
  };

  const handleLightTextLogoPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewLightTextLogoOpen(true);
  };

  const handleBackgroundPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewBackgroundOpen(true);
  };

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  const darkTextLogoPreviewUrl = getPreviewAssetUrl(profile?.text_logo_uri);
  const lightTextLogoPreviewUrl = getPreviewAssetUrl(profile?.light_text_logo_uri);
  const logoPreviewUrl = getPreviewAssetUrl(logoUrl || profile?.logo_uri);
  const backgroundPreviewUrl = getPreviewAssetUrl(backgroundUrl || profile?.background_uri);
  // Theme-aware logos for the new live preview surfaces. The normal logo uses
  // logo_light_uri on the light theme (falling back to the dark asset).
  const previewBrandColor = profile?.landing_color || DEFAULT_LANDING_COLOR;
  const themedNormalLogoPreviewUrl =
    portalPreviewTheme === 'light'
      ? getPreviewAssetUrl(profile?.logo_light_uri || profile?.logo_uri) || logoPreviewUrl
      : logoPreviewUrl;
  // Client portal header logo: mirror the real authenticated header, which
  // prefers the theme-aware normal brand logo (logo_uri / logo_light_uri) and
  // falls back to the theme-aware text logo. Honor the in-progress upload by
  // overriding logo_uri with the locally edited `logoUrl` when present.
  const themedHeaderTextLogoUrl =
    portalPreviewTheme === 'light'
      ? profile?.light_text_logo_uri || profile?.text_logo_uri
      : profile?.text_logo_uri || profile?.light_text_logo_uri;
  const themedHeaderLogoPreviewUrl = getPreviewAssetUrl(
    selectNormalBrandLogo(
      {
        logo_uri: logoUrl || profile?.logo_uri,
        logo_light_uri: profile?.logo_light_uri,
      },
      portalPreviewTheme,
    ) ||
      themedHeaderTextLogoUrl ||
      '',
  );
  const mobileHomeColor = getSolidPreviewColor(profile?.customize_color, DEFAULT_MOBILE_HOME_COLOR);
  const mobileCompanyNameColor = getSolidPreviewColor(
    profile?.name_color,
    DEFAULT_MOBILE_COMPANY_NAME_COLOR,
  );
  const mobileHomeSummaryStyle: React.CSSProperties = {
    backgroundColor: mobileHomeColor,
    '--mobile-home-background-image': backgroundPreviewUrl
      ? toCssUrl(backgroundPreviewUrl)
      : 'none',
  } as React.CSSProperties;
  const mobileLandingPreviewStyle: React.CSSProperties = {
    ...getPreviewBrandActionStyle({
      brandColor: profile?.landing_color || DEFAULT_LANDING_COLOR,
      surfaceColor: '#000000',
      pageBackgroundColor: '#000000',
    }),
    '--mobile-landing-background-image': backgroundPreviewUrl
      ? toCssUrl(backgroundPreviewUrl)
      : 'none',
  } as React.CSSProperties;

  return (
    <div className="profile company-settings-page">
      <Spin spinning={loading} tip="Loading data...">
        <div className="container">
          <div className="content">
            <div className="company-settings-actions">
              {edit ? (
                <>
                  <Button className="cs-action-btn cs-action-ghost" onClick={cancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    className="cs-action-btn cs-action-primary"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button className="cs-action-btn cs-action-ghost" onClick={handleSave}>
                  Update
                </Button>
              )}
            </div>
            <div className="settings-tabs" role="tablist">
              {COMPANY_SETTINGS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`settings-tab${activeTab === tab.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'brand-identity' && (
              <div className="settings-section" role="tabpanel">
                <div className="section-header">
                  <div className="section-title">Brand Identity</div>
                  <div className="section-subtitle">
                    Core company information used across branded experiences.
                  </div>
                </div>
                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Company Name
                      <div className="explain">
                        Shown in portal surfaces and branded experiences.
                      </div>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.name
                      ) : (
                        <Input
                          defaultValue={profile?.name}
                          onChange={(e) =>
                            setProfile((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="inline">
                    <div className="key">
                      Firm Alias
                      <div className="explain">
                        Used for branded URLs. 3–32 lowercase letters, numbers, or hyphens.
                      </div>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.alias || 'No alias set'
                      ) : (
                        <>
                          <Input
                            data-testid="firm-alias-input"
                            placeholder="e.g. clayvvest"
                            maxLength={FIRM_ALIAS_MAX_LENGTH}
                            defaultValue={profile?.alias ?? ''}
                            status={aliasError ? 'error' : undefined}
                            onChange={(e) => {
                              const nextAlias = e.target.value.trim().toLowerCase();
                              setProfile((prev) => (prev ? { ...prev, alias: nextAlias } : prev));
                              if (aliasError) {
                                setAliasError(null);
                              }
                            }}
                          />
                          {aliasError && (
                            <div
                              role="alert"
                              data-testid="firm-alias-error"
                              style={{ color: '#ff4d4f', marginTop: 4, fontSize: 12 }}
                            >
                              {aliasError}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="firm-alias-warning" role="note">
                      <WarningFilled className="firm-alias-warning-icon" aria-hidden="true" />
                      <span>
                        Firm Alias is a unique identifier and should not be changed once in use.
                        Updating it will affect your custom login, signup, and authentication URLs.
                        If you’re unsure, do not make this change.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="name">
                  <div className="key">Apex Correspondent ID</div>
                  <div className="value">{profile?.apex_correspondent_id}</div>
                </div>
                <div className="name">
                  <div className="key">
                    Introduction
                    <div className="explain">Used in signup and branded onboarding surfaces.</div>
                  </div>
                  <div className="value">
                    {!edit ? (
                      profile?.introduction
                    ) : (
                      <Input.TextArea
                        rows={6}
                        defaultValue={profile?.introduction}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev ? { ...prev, introduction: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </div>
                </div>
                <div className="name default-theme">
                  <div className="key">
                    Default Theme
                    <div className="explain">
                      Used as the default theme for first-time users who have not selected a
                      personal theme preference.
                    </div>
                  </div>
                  <div className="value">
                    <div className="theme-options" role="radiogroup" aria-label="Default theme">
                      {DEFAULT_THEME_OPTIONS.map((option) => {
                        const selected = themeSelection === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={option.label}
                            disabled={!edit}
                            className={`theme-option${selected ? ' selected' : ''}`}
                            onClick={() => handleThemeSelect(option.key)}
                          >
                            <span className="theme-preview-frame">
                              <ThemePreviewMock variant={option.variant} />
                            </span>
                            <span className="theme-option-label">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'authentication' && (
              <div className="settings-section" role="tabpanel">
                <div className="section-header">
                  <div className="section-title">Authentication Experience</div>
                  <div className="section-subtitle">
                    Configure branding for login and signup experiences across desktop and mobile.
                  </div>
                </div>
                <div className="name">
                  <div className="key">
                    Login Background Image
                    <AssetSpec>
                      Recommended: PNG or JPG · Minimum size: 550 × 570 px · Images will be cropped
                      across different screen sizes. Keep important content centered.
                    </AssetSpec>
                  </div>
                  <div className="value">
                    {!edit ? (
                      profile?.background_uri ? (
                        <div
                          className="preview-icon"
                          style={{
                            backgroundImage: `url("${profile?.background_uri}?t=${Date.now()}")`,
                          }}
                        />
                      ) : (
                        'No landing image uploaded'
                      )
                    ) : (
                      <>
                        {profile?.background_uri ? (
                          <AntdImg
                            wrapperStyle={{ display: 'none' }}
                            preview={{
                              visible: previewBackgroundOpen,
                              onVisibleChange: (visible) => setPreviewBackgroundOpen(visible),
                            }}
                            src={profile.background_uri}
                          />
                        ) : (
                          'No preview available'
                        )}
                        <Upload
                          listType="picture-card"
                          fileList={backgroundFileList}
                          onPreview={handleBackgroundPreview}
                          onChange={handleBackgroundUploadChange}
                          customRequest={(options: UploadProps) => {
                            handleFileUpload(
                              options as {
                                file: RcFile;
                                onSuccess?: () => void;
                                onError?: (error: Error) => void;
                              },
                              'background',
                            );
                          }}
                          maxCount={1}
                        >
                          <UploadTrigger />
                        </Upload>
                      </>
                    )}
                  </div>
                  <AssetUsage groups={LOGIN_BACKGROUND_USAGE} />
                </div>
              </div>
            )}

            {activeTab === 'brand-colors' && (
              <div className="settings-section" role="tabpanel">
                <div className="section-header">
                  <div className="section-title">Brand Colors</div>
                  <div className="section-subtitle">
                    Choose colors that reflect your brand while maintaining readability across light
                    and dark themes.
                  </div>
                </div>
                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Client portal primary color
                      <div className="explain">
                        Used for login/signup buttons and landing accents
                      </div>
                    </div>
                    <div className="value color-picker">
                      <ColorPicker
                        value={profile?.landing_color}
                        disabled={!edit}
                        showText={(color) => formatBrandColorText(color as BrandColorValue)}
                        onChange={(color) => {
                          setProfile((prev) =>
                            prev ? { ...prev, landing_color: clampColorAlpha(color) } : prev,
                          );
                        }}
                      />
                    </div>
                    <AppliedTo
                      items={[
                        'Primary CTA buttons',
                        'Active / selected states',
                        'Tabs',
                        'Icons',
                        'Borders',
                        'Navigation highlights',
                      ]}
                    />
                  </div>
                  <div className="inline">
                    <div className="key">
                      Client portal secondary color
                      <div className="explain">Used for secondary buttons and landing accents</div>
                    </div>
                    <div className="value color-picker">
                      <ColorPicker
                        value={profile?.secondary_color}
                        disabled={!edit}
                        showText={(color) => formatBrandColorText(color as BrandColorValue)}
                        onChange={(color) => {
                          setProfile((prev) =>
                            prev ? { ...prev, secondary_color: clampColorAlpha(color) } : prev,
                          );
                        }}
                      />
                    </div>
                    <AppliedTo items={['Supporting accents', 'Empty state illustration']} />
                  </div>
                </div>
                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Mobile app home color
                      <div className="explain">Used on the mobile app home summary card</div>
                    </div>
                    <div className="value color-picker">
                      <ColorPicker
                        value={profile?.customize_color}
                        disabled={!edit}
                        showText={(color) => formatBrandColorText(color as BrandColorValue)}
                        onChange={(color) => {
                          setProfile((prev) =>
                            prev ? { ...prev, customize_color: clampColorAlpha(color) } : prev,
                          );
                        }}
                      />
                    </div>
                    <AppliedTo items={['Mobile app home summary card background']} />
                  </div>
                  <div className="inline">
                    <div className="key">
                      Mobile app company name color
                      <div className="explain">Used on the company name in the home card</div>
                    </div>
                    <div className="value color-picker">
                      <ColorPicker
                        value={profile?.name_color}
                        disabled={!edit}
                        showText={(color) => formatBrandColorText(color as BrandColorValue)}
                        onChange={(color) => {
                          setProfile((prev) =>
                            prev ? { ...prev, name_color: clampColorAlpha(color) } : prev,
                          );
                        }}
                      />
                    </div>
                    <AppliedTo items={['Company name on the mobile home card']} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'logos' && (
              <div className="settings-section" role="tabpanel">
                <div className="section-header">
                  <div className="section-title">Logos</div>
                  <div className="section-subtitle">
                    Upload separate logo assets for light and dark surfaces to avoid low contrast or
                    squeezed rendering.
                  </div>
                </div>
                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Primary Logo - Dark Surfaces
                      <div className="explain">Used in the login hero</div>
                      <AssetSpec>{PRIMARY_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.logo_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.logo_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No landing logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.logo_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewLogoOpen,
                                onVisibleChange: (visible) => setPreviewLogooOpen(visible),
                              }}
                              src={profile.logo_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={logoFileList}
                            onPreview={handleLogoPreview}
                            onChange={handleLogoUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'logo',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={PRIMARY_LOGO_USAGE} />
                  </div>
                  <div className="inline">
                    <div className="key">
                      Primary Logo - Light Surfaces
                      <div className="explain">
                        Light version of the landing logo for the login hero
                      </div>
                      <AssetSpec>{PRIMARY_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.logo_light_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.logo_light_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No light landing logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.logo_light_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewLogoLightOpen,
                                onVisibleChange: (visible) => setPreviewLogoLightOpen(visible),
                              }}
                              src={profile.logo_light_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={logoLightFileList}
                            onPreview={handleLogoLightPreview}
                            onChange={handleLogoLightUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'logo_light',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={PRIMARY_LOGO_USAGE} />
                  </div>
                </div>

                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Header logo - Dark Surfaces
                      <div className="explain">Used in the client portal header on dark theme</div>
                      <AssetSpec>{HEADER_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.text_logo_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.text_logo_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No dark theme header logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.text_logo_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewTextLogoOpen,
                                onVisibleChange: (visible) => setPreviewTextLogoOpen(visible),
                              }}
                              src={profile.text_logo_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={textLogoFileList}
                            onPreview={handleTextLogoPreview}
                            onChange={handleTextLogoUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'text_logo',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={HEADER_LOGO_USAGE} />
                  </div>
                  <div className="inline">
                    <div className="key">
                      Header logo - Light Surfaces
                      <div className="explain">Used in the client portal header on light theme</div>
                      <AssetSpec>{HEADER_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.light_text_logo_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.light_text_logo_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No light theme header logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.light_text_logo_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewLightTextLogoOpen,
                                onVisibleChange: (visible) => setPreviewLightTextLogoOpen(visible),
                              }}
                              src={profile.light_text_logo_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={lightTextLogoFileList}
                            onPreview={handleLightTextLogoPreview}
                            onChange={handleLightTextLogoUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'light_text_logo',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={HEADER_LOGO_USAGE} />
                  </div>
                </div>

                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Mobile compact logo mark
                      <div className="explain">Used for compact app and account icons</div>
                      <AssetSpec>{MARK_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.logo_mark_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.logo_mark_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No compact logo mark uploaded'
                        )
                      ) : (
                        <>
                          {profile?.logo_mark_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewLogooMarkOpen,
                                onVisibleChange: (visible) => setPreviewLogooMarkOpen(visible),
                              }}
                              src={profile.logo_mark_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={logoMarkFileList}
                            onPreview={handleLogooMarkPreview}
                            onChange={handleLogooMarkUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'logo_mark',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={MARK_LOGO_USAGE} />
                  </div>
                  <div className="inline" />
                </div>

                <div className="name flex">
                  <div className="inline">
                    <div className="key">
                      Compact logo - Dark Surfaces
                      <div className="explain">Used for compact app and account icons</div>
                      <AssetSpec>{COMPACT_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.compact_logo_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.compact_logo_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No compact logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.compact_logo_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewCompactLogoOpen,
                                onVisibleChange: (visible) => setPreviewCompactLogoOpen(visible),
                              }}
                              src={profile.compact_logo_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={compactLogoFileList}
                            onPreview={handleCompactLogoPreview}
                            onChange={handleCompactLogoUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'compact_logo',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={COMPACT_LOGO_USAGE} />
                  </div>
                  <div className="inline">
                    <div className="key">
                      Compact logo - Light Surfaces
                      <div className="explain">Light version of the compact logo</div>
                      <AssetSpec>{COMPACT_LOGO_SPEC}</AssetSpec>
                    </div>
                    <div className="value">
                      {!edit ? (
                        profile?.compact_logo_light_uri ? (
                          <div
                            className="preview-icon"
                            style={{
                              backgroundImage: `url("${profile?.compact_logo_light_uri}?t=${Date.now()}")`,
                            }}
                          />
                        ) : (
                          'No light compact logo uploaded'
                        )
                      ) : (
                        <>
                          {profile?.compact_logo_light_uri ? (
                            <AntdImg
                              wrapperStyle={{ display: 'none' }}
                              preview={{
                                visible: previewCompactLogoLightOpen,
                                onVisibleChange: (visible) =>
                                  setPreviewCompactLogoLightOpen(visible),
                              }}
                              src={profile.compact_logo_light_uri}
                            />
                          ) : (
                            'No preview available'
                          )}
                          <Upload
                            listType="picture-card"
                            fileList={compactLogoLightFileList}
                            onPreview={handleCompactLogoLightPreview}
                            onChange={handleCompactLogoLightUploadChange}
                            customRequest={(options: UploadProps) => {
                              handleFileUpload(
                                options as {
                                  file: RcFile;
                                  onSuccess?: () => void;
                                  onError?: (error: Error) => void;
                                },
                                'compact_logo_light',
                              );
                            }}
                            maxCount={1}
                          >
                            <UploadTrigger />
                          </Upload>
                        </>
                      )}
                    </div>
                    <AssetUsage groups={COMPACT_LOGO_USAGE} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="preview live-preview">
            <div className="key preview-header live-preview-header">
              <span>Live Preview</span>
              <Segmented<PortalPreviewTheme>
                size="small"
                value={livePreviewTab === 'authentication' ? 'dark' : portalPreviewTheme}
                onChange={setPortalPreviewTheme}
                options={
                  livePreviewTab === 'authentication'
                    ? [{ label: 'Dark', value: 'dark' }]
                    : [
                        { label: 'Dark', value: 'dark' },
                        { label: 'Light', value: 'light' },
                      ]
                }
              />
            </div>
            <div className="live-preview-tabs">
              {LIVE_PREVIEW_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={livePreviewTab === tab.key}
                  className={`live-preview-tab${livePreviewTab === tab.key ? ' active' : ''}`}
                  onClick={() => setLivePreviewTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="value">
              {livePreviewTab === 'authentication' && (
                <>
                  <div className="preview-section">
                    <div className="preview-section-title">Client portal sign in</div>
                    <div className="portal-preview-grid">
                      <ClientPortalBrandingPreview
                        backgroundUrl={backgroundPreviewUrl}
                        heroBackgroundImage={AUTH_PREVIEW_HERO_BACKGROUND}
                        lightTextLogoUrl={lightTextLogoPreviewUrl}
                        profile={profile}
                        logoUrl={logoPreviewUrl}
                        textLogoUrl={darkTextLogoPreviewUrl}
                        variant="login"
                        viewport="desktop"
                        themeMode="dark"
                      />
                      <ClientPortalBrandingPreview
                        backgroundUrl={backgroundPreviewUrl}
                        heroBackgroundImage={AUTH_PREVIEW_HERO_BACKGROUND}
                        lightTextLogoUrl={lightTextLogoPreviewUrl}
                        profile={profile}
                        logoUrl={logoPreviewUrl}
                        textLogoUrl={darkTextLogoPreviewUrl}
                        variant="login"
                        viewport="mobile"
                        themeMode="dark"
                      />
                    </div>
                  </div>
                  <div className="preview-section">
                    <div className="preview-section-title">Advisor portal sign up</div>
                    <div className="portal-preview-grid">
                      <ClientPortalBrandingPreview
                        backgroundUrl={backgroundPreviewUrl}
                        heroBackgroundImage={AUTH_PREVIEW_HERO_BACKGROUND}
                        lightTextLogoUrl={lightTextLogoPreviewUrl}
                        profile={profile}
                        logoUrl={logoPreviewUrl}
                        textLogoUrl={darkTextLogoPreviewUrl}
                        variant="signup"
                        viewport="desktop"
                        themeMode="dark"
                      />
                      <ClientPortalBrandingPreview
                        backgroundUrl={backgroundPreviewUrl}
                        heroBackgroundImage={AUTH_PREVIEW_HERO_BACKGROUND}
                        lightTextLogoUrl={lightTextLogoPreviewUrl}
                        profile={profile}
                        logoUrl={logoPreviewUrl}
                        textLogoUrl={darkTextLogoPreviewUrl}
                        variant="signup"
                        viewport="mobile"
                        themeMode="dark"
                      />
                    </div>
                  </div>
                </>
              )}
              {livePreviewTab === 'advisor-portal' && (
                <>
                  <div className="preview-section">
                    <div className="preview-section-title">Navigation &amp; branding</div>
                    <div className="portal-preview-grid">
                      <AdvisorNavBrandingPreview
                        profile={profile}
                        logoUrl={themedNormalLogoPreviewUrl}
                        themeMode={portalPreviewTheme}
                        brandColor={previewBrandColor}
                      />
                    </div>
                  </div>
                  <div className="preview-section">
                    <div className="preview-section-title">Brand color usage</div>
                    <div className="portal-preview-grid">
                      <BrandColorUsagePreview
                        themeMode={portalPreviewTheme}
                        brandColor={previewBrandColor}
                      />
                    </div>
                  </div>
                </>
              )}
              {livePreviewTab === 'client-portal' && (
                <>
                  <div className="preview-section">
                    <div className="preview-section-title">Header &amp; brand color usage</div>
                    <div className="portal-preview-grid">
                      <ClientPortalHeaderPreview
                        profile={profile}
                        textLogoUrl={themedHeaderLogoPreviewUrl}
                        themeMode={portalPreviewTheme}
                        brandColor={previewBrandColor}
                      />
                    </div>
                  </div>
                  <div className="preview-section">
                    <div className="preview-section-title">Mobile app</div>
                    <div className="mobile-app-preview-grid">
                      {profile?.co_branding && (
                        <div
                          className="preview-card sign"
                          style={
                            {
                              ...mobileLandingPreviewStyle,
                              backgroundColor: '#000000',
                            } as React.CSSProperties
                          }
                        >
                          <div
                            className="icon"
                            style={{
                              backgroundImage: logoUrl
                                ? `url("${logoUrl}?t=${Date.now()}")`
                                : 'none',
                            }}
                          />
                          <div className="button">Sign up</div>
                        </div>
                      )}
                      {profile?.co_branding && (
                        <div className="preview-card loadPage" style={mobileLandingPreviewStyle}>
                          <div className="icons">
                            <div
                              className="ria icon"
                              style={{
                                backgroundImage: logoMarkUrl
                                  ? `url("${logoMarkUrl}?t=${Date.now()}")`
                                  : 'none',
                              }}
                            />
                            <div
                              className="wav icon"
                              style={{ backgroundImage: `url(${WavvestLogo})` }}
                            />
                          </div>
                          <div className="text">Welcome, user name.</div>
                        </div>
                      )}
                      <div className="preview-card homePage">
                        {profile?.co_branding ? (
                          <div className="icons">
                            <div
                              className="ria icon"
                              style={{
                                backgroundImage: logoMarkUrl
                                  ? `url("${logoMarkUrl}?t=${Date.now()}")`
                                  : 'none',
                              }}
                            />
                            <div
                              className="wav icon"
                              style={{ backgroundImage: `url(${WavvestLogo})` }}
                            />
                          </div>
                        ) : (
                          <div className="icons">
                            <div
                              className="logoWithText"
                              style={{ backgroundImage: `url(${WavvestLogoWithText})` }}
                            />
                          </div>
                        )}

                        <div className="top-section" style={mobileHomeSummaryStyle}>
                          <div className="total-investing">
                            <div className="section-name">Total investing</div>
                            <div className="big_number">$200,600.66</div>
                            <div style={{ color: 'green' }}>+ $21.71 (3.1%) 1D</div>
                          </div>
                          <div
                            className="co_name"
                            style={{
                              color: mobileCompanyNameColor,
                            }}
                          >
                            {profile?.name}
                          </div>
                        </div>

                        <div className="middle-section">
                          <div className="item">
                            <div className="icon-container">
                              <img src={money} alt="Investment Icon" />
                            </div>
                            <div className="text">
                              <p>Investments</p>
                              <p>$200,600.66</p>
                            </div>
                            <div className="arrow-right">&gt;</div>
                          </div>
                          <div className="item">
                            <div className="icon-container">
                              <img src={shieldCheck} alt="Estate Planning Icon" />
                            </div>
                            <div className="text">
                              <p>Estate planning</p>
                            </div>
                            <div className="arrow-right">&gt;</div>
                          </div>
                          <div className="item">
                            <div className="icon-container">
                              <img src={umbrella} alt="Insurance Icon" />
                            </div>
                            <div className="text">
                              <p>Insurance</p>
                            </div>
                            <div className="arrow-right">&gt;</div>
                          </div>
                          <div className="item">
                            <div className="icon-container">
                              <img src={fileImg} alt="Taxes Icon" />
                            </div>
                            <div className="text">
                              <p>Taxes</p>
                            </div>
                            <div className="arrow-right">&gt;</div>
                          </div>
                          <div className="item">
                            <div className="icon-container">
                              <img src={lineChartImg} alt="Financial Planning Icon" />
                            </div>
                            <div className="text">
                              <p>Financial planning</p>
                            </div>
                            <div className="arrow-right">&gt;</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default CompanySettings;
